import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, Button, Select, Input } from 'antd';
import { QrcodeOutlined, DownloadOutlined, FilePdfOutlined } from '@ant-design/icons';

const BRAND = '#DB814F';
const BRAND_LIGHT = '#fff5ef';
const BRAND_BORDER = '#f5cdb3';

const baseUrl = process.env.REACT_APP_API_ENDPOINT || '';

const RETURN_REASONS = [
  'Damaged in transit',
  'Wrong item sent',
  'Faulty or defective',
  'No longer wanted',
  'Ordered by mistake',
  'Other',
];

// Stored paths come back as "/uploads/...", but Royal Mail may hand back a hosted URL.
const toFileUrl = (storedPath) =>
  !storedPath || /^https?:\/\//i.test(storedPath) ? storedPath : `${baseUrl}${storedPath}`;

const downloadFile = (storedPath) => {
  const url = toFileUrl(storedPath);
  if (!url) return;

  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('target', '_blank');
  link.setAttribute('download', url.split('/').pop());
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const ReturnQrModal = ({ open, onCancel, returnData, onCreate, creating }) => {
  const [reason, setReason] = useState(null);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (!open) return;
    setReason(null);
    setNotes('');
  }, [open]);

  const notesRequired = reason === 'Other';
  const canCreate = reason && (!notesRequired || notes.trim());

  const qrUrl = toFileUrl(returnData?.qrUrl);
  const labelUrl = toFileUrl(returnData?.labelUrl);
  const isQrImage = qrUrl && !/\.pdf$/i.test(qrUrl);

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span
            style={{
              width: 32, height: 32, borderRadius: 8,
              background: BRAND_LIGHT, border: `1px solid ${BRAND_BORDER}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <QrcodeOutlined style={{ color: BRAND, fontSize: 16 }} />
          </span>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15, lineHeight: 1.3 }}>QR Code Return</div>
            <div style={{ fontSize: 11, color: '#999', fontWeight: 400 }}>
              {returnData?.trackingNumber
                ? `Tracking ${returnData.trackingNumber}`
                : 'Royal Mail return'}
            </div>
          </div>
        </div>
      }
      open={open}
      onCancel={onCancel}
      footer={null}
      width={440}
    >
      {!returnData && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '4px 0 8px' }}>
          <div style={{ fontSize: 12, color: '#999' }}>
            Royal Mail charges for every return generated.
          </div>
          <Select
            placeholder="Reason for return"
            value={reason}
            onChange={setReason}
            options={RETURN_REASONS.map((value) => ({ value, label: value }))}
            aria-label="Reason for return"
          />
          <Input.TextArea
            rows={3}
            maxLength={500}
            placeholder={notesRequired ? 'Notes (required)' : 'Notes (optional)'}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            aria-label="Return notes"
          />
          <Button
            type="primary"
            block
            icon={<QrcodeOutlined />}
            disabled={!canCreate}
            loading={creating}
            onClick={() => onCreate({ reason, notes: notes.trim() })}
          >
            Generate QR Return
          </Button>
        </div>
      )}

      {returnData && (
      <div style={{ padding: '4px 0 8px' }}>
        {isQrImage && (
          <div
            style={{
              display: 'flex', justifyContent: 'center', marginBottom: 20, padding: 16,
              background: '#fafafa', border: '1px solid #f0f0f0', borderRadius: 8,
            }}
          >
            <img
              src={qrUrl}
              alt="Royal Mail return QR code"
              style={{ maxWidth: 200, width: '100%', imageRendering: 'pixelated' }}
            />
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <Button
            block
            icon={<DownloadOutlined />}
            disabled={!qrUrl}
            onClick={() => downloadFile(returnData?.qrUrl)}
          >
            Download QR Code
          </Button>
          <Button
            block
            icon={<FilePdfOutlined />}
            disabled={!labelUrl}
            onClick={() => downloadFile(returnData?.labelUrl)}
          >
            Download Return Label
          </Button>
        </div>
      </div>
      )}
    </Modal>
  );
};

ReturnQrModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  returnData: PropTypes.shape({
    trackingNumber: PropTypes.string,
    qrUrl: PropTypes.string,
    labelUrl: PropTypes.string,
  }),
  onCreate: PropTypes.func,
  creating: PropTypes.bool,
};

export default ReturnQrModal;
